import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as classes from './Select.module.scss';
import cn from 'classnames';
import { SelectOption } from '../../../interfaces/SelectOption';
import DropdownArrow from '../../../assets/icons/inline/lang-dropdown-arrow.inline.svg';
import Option from './Option/Option';

interface Props {
  options: Array<SelectOption>;
  value?: SelectOption;
  inputClassName?: string;
  listClassName?: string;
  optionClassName?: string;
  wrapperClassName?: string;
  dropDownIconClassName?: string;
  onChangeAction: (val: SelectOption) => void;
  dropDownIcon?: JSX.Element | null;
}

const Select: FC<Props> = ({
  inputClassName,
  listClassName,
  optionClassName,
  wrapperClassName,
  options,
  value,
  onChangeAction,
  dropDownIcon,
  dropDownIconClassName,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState<string | boolean | number>();
  const ref = useRef<HTMLDivElement>(null);

  const toggleHandler = useCallback(() => setIsOpen(!isOpen), [isOpen]);

  useEffect(() => {
    document.addEventListener('keydown', handlePushEscape, true);
    document.addEventListener('click', handleClickOutside, true);
    return () => {
      document.removeEventListener('keydown', handlePushEscape, true);
      document.removeEventListener('click', handleClickOutside, true);
    };
  });

  const handleClickOutside = (event: MouseEvent) => {
    if (ref.current && !ref.current.contains(event.target as Node)) {
      setIsOpen(false);
    }
  };

  const handlePushEscape = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      setIsOpen(false);
    }
  };

  const setSelectValue = useCallback((value: SelectOption) => {
    if (value.additionalLabel) {
      setSelectedOption(value.additionalLabel);
    }
    if (value.label && !value.additionalLabel) {
      setSelectedOption(value.label);
    }
    if (!value.label && !value.additionalLabel) {
      setSelectedOption(value.value);
    }
    setIsOpen(false);
  }, []);

  useEffect(() => {
    const val = options.find(option => option.value === value?.value);
    if (val) {
      setSelectValue(val);
    }
  }, [setSelectValue, options, value]);

  const optionClickHandler = useCallback(
    (val: SelectOption) => {
      setSelectValue(val);
      onChangeAction(val);
    },
    [onChangeAction, setSelectValue],
  );

  const optionsMapper = useCallback(
    (option: SelectOption) => (
      <Option
        option={option}
        className={optionClassName}
        clickAction={optionClickHandler}
        key={option.value ? option.value : option.label}
        selected={option.value === value?.value}
      >
        {option.label || option.value}
      </Option>
    ),
    [optionClassName, optionClickHandler, value],
  );

  const optionsList = useMemo(() => {
    return <div className={cn(classes.itemsList, listClassName)}>{options.map(optionsMapper)}</div>;
  }, [listClassName, options, optionsMapper]);

  const dropDownArrow = useMemo(() => {
    if (dropDownIcon) {
      return dropDownIcon;
    }
    return <DropdownArrow />;
  }, [dropDownIcon]);

  return (
    <div className={cn(classes.wrapper, wrapperClassName)} onClick={toggleHandler} ref={ref}>
      <div className={inputClassName}>{selectedOption}</div>
      {isOpen && optionsList}
      <div className={cn(classes.dropDownIconWrapper, isOpen && classes.isOpen, dropDownIconClassName)}>{dropDownArrow}</div>
    </div>
  );
};

export default Select;
